/**
 * The Supabase I/O for sync: push a room's rows up, pull rooms down.
 *
 * The sync UNIT is the ROOM (with its walls and obstructions as replaceable
 * contents), not the individual wall — because wall ids are regenerated on
 * every cleanup, so per-wall diffing is meaningless. A push replaces a room's
 * walls/obstructions wholesale; conflict resolution compares the room's
 * server `updated_at`. See ADR 0022.
 *
 * All access is through the passed-in supabase-js client (schema 'sketch'),
 * which is the only place credentials live (ADR 0018). RLS scopes everything to
 * the signed-in owner; this module never sends an owner filter of its own.
 */

/**
 * Push one room's rows. Upserts the project and room, then REPLACES the room's
 * walls and obstructions (delete-all-then-insert), so a wall removed locally
 * does not linger in the cloud. Returns the room's server `updated_at`.
 *
 * @param {object} supabase
 * @param {{project:object, room:object, walls:object[], obstructions:object[]}} rows
 * @returns {Promise<{ok:true, updatedAt:string} | {ok:false, error:string}>}
 */
export async function pushRoom(supabase, rows) {
  const s = supabase.schema('sketch');
  try {
    // Project first (walls FK through room → project). Upsert on id: the id is
    // deterministic, so concurrent devices converge on one row.
    let r = await s.from('projects').upsert(rows.project, { onConflict: 'id' });
    if (r.error) return fail('projects', r.error);

    r = await s.from('rooms').upsert(rows.room, { onConflict: 'id' });
    if (r.error) return fail('rooms', r.error);

    // Replace the room's contents wholesale. Deleting the room's walls cascades
    // to their obstructions (wall_obstructions.wall_id ON DELETE CASCADE), so a
    // single scoped delete clears both; then re-insert the current set. Scoped
    // by room_id, so nothing outside this room is touched.
    r = await s.from('walls').delete().eq('room_id', rows.room.id);
    if (r.error) return fail('walls delete', r.error);

    if (rows.walls.length) {
      r = await s.from('walls').insert(rows.walls);
      if (r.error) return fail('walls insert', r.error);
    }
    if (rows.obstructions.length) {
      r = await s.from('wall_obstructions').insert(rows.obstructions);
      if (r.error) return fail('obstructions insert', r.error);
    }

    // Read back the room's server updated_at for the sync cursor.
    const read = await s.from('rooms').select('updated_at').eq('id', rows.room.id).single();
    return { ok: true, updatedAt: read.data?.updated_at ?? null };
  } catch (err) {
    return fail('push', err);
  }
}

/**
 * PostgREST silently caps every response at the server's max_rows (1,000 by
 * default; dashboard-managed on hosted, `supabase/config.toml` pins the
 * local stack). The cap is invisible — no error, no flag — and this client
 * sent no bound of its own (verified against the real supabase-js wire
 * shape, 2026-08-28: no Range header, no limit param). A truncated pull
 * assembled plausible partial rooms, was applied locally, and the wholesale
 * delete-then-insert push then wrote the truncation back as the truth.
 *
 * So: every bulk read pages until a SHORT page proves the set complete, and
 * a failure on ANY page fails the whole read — the caller applies nothing
 * rather than partial rooms. PAGE sits under any plausible cap so the loop,
 * never the cap, ends a read.
 *
 * KEYSET, not offsets (Coordinator's correction, 2026-08-28). Offset paging
 * introduced its own hazard: a row whose `updated_at` jumps mid-pull shifts
 * every later row back one slot, sliding the first row of the next page into
 * the already-consumed window — that DISPLACED row is skipped, its cursor
 * never catches it, and the wholesale push later writes the stale copy over
 * the server's good one. Same ending as the truncation bug, different road.
 * Keyset pages by "strictly greater than the last row's (updated_at, id)"
 * — there is no offset to shift, so a mid-pull write cannot displace
 * anything, and the id tiebreak is the pagination key itself: load-bearing,
 * not defence-in-depth. Values are quoted in the composite filter so
 * timestamps with reserved characters survive PostgREST's `or=` grammar.
 */
const PAGE = 500;
/** Id-list filters ride the URL; chunk them so the request stays bounded. */
const IN_CHUNK = 100;

/** Did this page move past the last one? The keyset's whole premise. */
function advanced(prev, next, keyA, keyB) {
  if (next[keyA] > prev[keyA]) return true;
  if (next[keyA] !== prev[keyA]) return false;
  return keyB ? next[keyB] > prev[keyB] : false;
}

/**
 * Page a bulk read to completion, keyset-style. EXPORTED so every unbounded
 * read in this codebase uses this one implementation — a second copy is how a
 * fix like this survives in one file and not the next.
 */
export async function fetchAllKeyset(buildQuery, keyA, keyB = null) {
  const out = [];
  let last = null;
  for (;;) {
    let q = buildQuery();
    if (last) {
      q = keyB
        ? q.or(`${keyA}.gt."${last[keyA]}",and(${keyA}.eq."${last[keyA]}",${keyB}.gt."${last[keyB]}")`)
        : q.gt(keyA, last[keyA]);
    }
    const res = await q.limit(PAGE);
    if (res.error) return { error: res.error };
    const rows = res.data ?? [];
    out.push(...rows);
    // STOP ON AN EMPTY PAGE, NOT A SHORT ONE. "Shorter than PAGE means the end"
    // is only true while PAGE is below the server's cap, and that cap is
    // DASHBOARD-MANAGED on hosted — it is not in this repository and nobody on
    // this project owns it. Measured with the cap below PAGE, the short-page
    // test reproduced the exact bug this function exists to prevent:
    //
    //     cap 1000 → 1201 rooms      cap 300 →  300 rooms, ok: true
    //     cap  500 → 1201 rooms      cap 100 →  100 rooms, ok: true
    //
    // Silent, ok:true, no failing test anywhere — the original truncation
    // returning through its own fix. Waiting for an EMPTY page removes the
    // dependency on the cap entirely: keyset always advances, so the loop
    // terminates on any cap at all.
    //
    // THE COST, taken deliberately: one extra round-trip per bulk read,
    // including the common small case (ten rooms was one request, now two).
    // The alternative — ask for an exact count and compare — saves that request
    // but makes the server count the table on every pull, so it gets expensive
    // exactly when the data is big, which is exactly when this matters. A
    // constant cost beats a scaling one, and both beat trusting a dashboard.
    if (rows.length === 0) return { rows: out };

    // THE PAGE MUST ADVANCE, or this loop is the new failure mode. Waiting for
    // an empty page is only safe while every page strictly advances the keyset;
    // a server that ignored the filter — or a fake that repeats its last
    // response — would be served forever. Hanging is WORSE than the truncation
    // this replaced: truncation returns wrong data, a hang returns nothing and
    // takes the sync with it.
    //
    // So non-advancement is treated as a broken response and surfaced, which is
    // the same refuse-and-report posture as a failed page. It is reachable, not
    // theoretical: it is exactly what a repeating stub does.
    const next = rows[rows.length - 1];
    if (last && !advanced(last, next, keyA, keyB)) {
      return { error: { message: `pagination stalled: ${keyA} did not advance past ${last[keyA]}` } };
    }
    last = next;
  }
}

async function fetchAllByIds(s, table, col, ids) {
  const out = [];
  for (let i = 0; i < ids.length; i += IN_CHUNK) {
    const chunk = ids.slice(i, i + IN_CHUNK);
    const page = await fetchAllKeyset(() => s.from(table).select('*').in(col, chunk).order('id'), 'id');
    if (page.error) return page;
    out.push(...page.rows);
  }
  return { rows: out };
}

/**
 * Pull rooms updated since a cursor (ISO string, or null for everything).
 * Returns one bundle per room: { room, walls, obstructions }. RLS limits this
 * to the owner's rooms. Tombstones (deleted_at set) RIDE THE PULL — the same
 * ADR 0100 lesson relearned for rooms (A5, ADR 0104): a row a cursor-based
 * pull excludes never reaches a device at all, so a deleted_at filter here
 * would keep every already-held room alive forever on other devices. A
 * tombstoned room's bundle carries no children — it is the death
 * certificate, not the body.
 *
 * @returns {Promise<{ok:true, rooms:Array} | {ok:false, error:string}>}
 */
export async function pullRooms(supabase, sinceIso = null) {
  const s = supabase.schema('sketch');
  try {
    const roomsPage = await fetchAllKeyset(() => {
      let q = s.from('rooms').select('*')
        .order('updated_at', { ascending: true }).order('id');
      if (sinceIso) q = q.gt('updated_at', sinceIso);
      return q;
    }, 'updated_at', 'id');
    if (roomsPage.error) return fail('rooms pull', roomsPage.error);
    const rooms = roomsPage.rows;
    if (rooms.length === 0) return { ok: true, rooms: [] };

    const roomIds = rooms.filter((r) => !r.deleted_at).map((r) => r.id);
    const wallsPage = await fetchAllByIds(s, 'walls', 'room_id', roomIds);
    if (wallsPage.error) return fail('walls pull', wallsPage.error);
    const walls = wallsPage.rows;

    const wallIds = walls.map((w) => w.id);
    let obstructions = [];
    if (wallIds.length) {
      const obsPage = await fetchAllByIds(s, 'wall_obstructions', 'wall_id', wallIds);
      if (obsPage.error) return fail('obstructions pull', obsPage.error);
      obstructions = obsPage.rows;
    }

    const byRoom = new Map(rooms.map((r) => [r.id, { room: r, walls: [], obstructions: [] }]));
    for (const w of walls) byRoom.get(w.room_id)?.walls.push(w);
    const wallRoom = new Map(walls.map((w) => [w.id, w.room_id]));
    for (const o of obstructions) byRoom.get(wallRoom.get(o.wall_id))?.obstructions.push(o);

    return { ok: true, rooms: [...byRoom.values()] };
  } catch (err) {
    return fail('pull', err);
  }
}

/**
 * Pushes one room DELETE as a soft-delete UPDATE (A5, ADR 0104): sets
 * deleted_at, and the server's set_updated_at trigger advances updated_at so
 * the tombstone rides every other device's ordinary cursor pull. Never a row
 * DELETE — a removed row is invisible to cursor pulls, which is the exact
 * defect this closes. A room that was never synced matches zero rows and
 * succeeds: there is nothing to propagate. RLS scopes the update to the
 * owner's rooms, as everywhere else in this module.
 */
export async function pushRoomDelete(supabase, roomId) {
  const s = supabase.schema('sketch');
  try {
    const result = await s.from('rooms')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', roomId);
    if (result.error) return fail('rooms delete', result.error);
    return { ok: true };
  } catch (err) {
    return fail('rooms delete', err);
  }
}

/**
 * Pushes one measurement-session bundle (N1, ADR 0105). Parents first —
 * FKs and RLS both check through them: project (the ownership anchor), then
 * the session row whose client_state carries the WHOLE document, then the
 * relational projection replaced wholesale scoped to the session (the same
 * shape as a room's walls). The upsert always rewrites the session row, so
 * the set_updated_at trigger advances updated_at and the change rides every
 * other device's cursor pull even when only a child projection moved.
 */
export async function pushMeasurementSession(supabase, rows) {
  const s = supabase.schema('sketch');
  try {
    let r = await s.from('projects').upsert(rows.project, { onConflict: 'id' });
    if (r.error) return fail('measurement project', r.error);
    r = await s.from('measurement_sessions').upsert(rows.session, { onConflict: 'id' });
    if (r.error) return fail('measurement session', r.error);
    for (const [table, list] of [['measurement_slots', rows.slots], ['measurement_features', rows.features]]) {
      r = await s.from(table).delete().eq('session_id', rows.session.id);
      if (r.error) return fail(`${table} delete`, r.error);
      if (list.length) {
        r = await s.from(table).insert(list);
        if (r.error) return fail(`${table} insert`, r.error);
      }
    }
    return { ok: true };
  } catch (err) {
    return fail('measurement session push', err);
  }
}

/**
 * Pulls measurement sessions changed after the device cursor — the session
 * rows ONLY (client_state carries the document, so the projection tables are
 * never joined on pull), tombstones INCLUDED (the A5 lesson: a cursor pull
 * that filters deleted_at never delivers a delete).
 */
export async function pullMeasurementSessions(supabase, sinceIso = null) {
  const s = supabase.schema('sketch');
  try {
    const page = await fetchAllKeyset(() => {
      let query = s.from('measurement_sessions').select('*')
        .order('updated_at', { ascending: true }).order('id');
      if (sinceIso) query = query.gt('updated_at', sinceIso);
      return query;
    }, 'updated_at', 'id');
    if (page.error) return fail('measurement sessions pull', page.error);
    return { ok: true, rows: page.rows };
  } catch (err) {
    return fail('measurement sessions pull', err);
  }
}

/** Upserts an Engineering remeasure after its owning project/room are synced. */
export async function pushRemeasure(supabase, rows) {
  const s = supabase.schema('sketch');
  try {
    let result = await s.from('projects').upsert(rows.project, { onConflict: 'id' });
    if (result.error) return fail('remeasure project', result.error);
    result = await s.from('remeasure_sessions').upsert(rows.remeasure, { onConflict: 'id' });
    if (result.error) return fail('remeasure', result.error);
    return { ok: true, updatedAt: result.data?.updated_at ?? null };
  } catch (err) {
    return fail('remeasure push', err);
  }
}

/** Pulls all non-deleted remeasure records changed after the device cursor. */
export async function pullRemeasures(supabase, sinceIso = null) {
  const s = supabase.schema('sketch');
  try {
    const page = await fetchAllKeyset(() => {
      let query = s.from('remeasure_sessions').select('*').is('deleted_at', null)
        .order('updated_at', { ascending: true }).order('id');
      if (sinceIso) query = query.gt('updated_at', sinceIso);
      return query;
    }, 'updated_at', 'id');
    if (page.error) return fail('remeasure pull', page.error);
    return { ok: true, rows: page.rows };
  } catch (err) {
    return fail('remeasure pull', err);
  }
}

/**
 * Pushes one reusable-section row.
 *
 * No project hop — unlike every other push here, a reusable section belongs to
 * the DESIGNER, not to a project or a room (ADR 0100). RLS scopes it to
 * `auth.uid()`, and as everywhere else in this module no owner filter is sent.
 *
 * A DELETE is a push too: the row carries `deleted_at`, and the pull RETURNS
 * tombstones so the engine can remove the component on other devices. (The
 * first draft filtered tombstones out of the pull and claimed that was how
 * deletion propagated — falsified while wiring, 2026-08-29: a row a
 * cursor-based pull excludes never reaches a device at all, so a device that
 * already held the component would keep it forever. ADR 0100 amendment.)
 */
export async function pushReusableSection(supabase, row) {
  const s = supabase.schema('sketch');
  try {
    const result = await s.from('reusable_sections').upsert(row, { onConflict: 'id' });
    if (result.error) return fail('reusable section', result.error);
    // No updatedAt in the result on purpose: an upsert without .select()
    // returns no rows, so reading result.data here can only ever yield null
    // (found dead while wiring). The cursor advances from PULLED rows.
    return { ok: true };
  } catch (err) {
    return fail('reusable section push', err);
  }
}

/**
 * Pulls the designer's components changed after the device cursor —
 * INCLUDING tombstones, which is how a deletion reaches this device.
 */
export async function pullReusableSections(supabase, sinceIso = null) {
  const s = supabase.schema('sketch');
  try {
    const page = await fetchAllKeyset(() => {
      let query = s.from('reusable_sections').select('*')
        .order('updated_at', { ascending: true }).order('id');
      if (sinceIso) query = query.gt('updated_at', sinceIso);
      return query;
    }, 'updated_at', 'id');
    if (page.error) return fail('reusable section pull', page.error);
    return { ok: true, rows: page.rows };
  } catch (err) {
    return fail('reusable section pull', err);
  }
}

// platform-boundary-exception: extracts message text for a uniform result
// shape, no retry-policy branch — see platform/boundary-exceptions.json.
function fail(where, error) {
  const message = error?.message ?? String(error);
  return { ok: false, error: `${where}: ${message}` };
}
